import * as React from "react";
import { makeStyles, useTheme, Theme } from '@material-ui/core/styles';
import MenuItem from "@material-ui/core/MenuItem";
import IconButton from "@material-ui/core/IconButton";
import SupervisedUserCircleIcon from '@material-ui/icons/SupervisedUserCircle';
import { useTranslation } from 'react-i18next';

import { Medewerker } from "../../../entities/Medewerker";
import { Dialog, DialogTitle, DialogContent, Typography, DialogActions } from "@material-ui/core";
import { MedewerkerSelect } from "./MedewerkerSelect";
import { FetchState } from "../../../entities/FetchState";
import Skeleton from "@material-ui/lab/Skeleton";
import { IApplicationState } from "../../../store";
import ReplayIcon from '@material-ui/icons/Replay';
import { useSelector, useDispatch } from 'react-redux';
import * as Selectie from "../../../store/selectie";

// CSS
const useStyles = makeStyles(() => ({
    menu: {
        width: 300,
        maxWidth: '100%',
    },
    dialog: {
        minWidth: 300,
    },
 }));

interface ICommonProps {
   label?: string;
}

// All the incoming values
interface IProps extends ICommonProps {
    selectedMedewerker?: Medewerker;
    syncWithRedux?: boolean;
}

interface ICommonDispatch {
    onChange?: (staff?: Medewerker) => void;
}
// Outgoing functions
interface IDispatch extends ICommonDispatch {
    
}

// Actual Component
export type AllProps = IProps & IDispatch;
export const MedewerkerToolbarSelect = (props: AllProps) => {
    const classes = useStyles(useTheme());
    const { t: tDatatypes } = useTranslation('datatypes');
    const { t: tCommon } = useTranslation('common');

    // Get Redux state and dispatch for internal use
    const medewerkersList = useSelector((state: IApplicationState) => state.selectie.allMedewerkers);
    const dispatch = useDispatch();

    const emptyLabel = props.label || tDatatypes('medewerkerLabel') || "Medewerker";

    const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null);
    const [text, setText] = React.useState(props.selectedMedewerker ? props.selectedMedewerker.fullName : emptyLabel);
    const [selectedMedewerker, setSelectedMedewerker] = React.useState(props.selectedMedewerker);

    const handleProfileMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
        setAnchorEl(event.currentTarget);
    };

    React.useEffect(() => {
        if (medewerkersList.fetchState === FetchState.NotFetched) {
            dispatch(Selectie.SelectieActionCreator.FetchAllMedewerkers());
        }
    }, [dispatch, medewerkersList.fetchState]);

    if (medewerkersList.fetchState === FetchState.NotFetched || medewerkersList.fetchState === FetchState.Busy) {
        return <MenuItem disabled>
            <Skeleton variant="text" width={100} />
            <IconButton
            aria-label={props.label || tDatatypes('medewerkerLabel')}
            aria-haspopup="true"
            color="inherit"
            >
                <Skeleton variant="circle" width={30} height={30} />
            </IconButton>
        </MenuItem>
    }

    if (medewerkersList.fetchState === FetchState.Error) {
        return <MenuItem>
            <Typography variant="body1" color="error">
                {tCommon('genericError')}
            </Typography>
            <IconButton
            aria-label={props.label || tDatatypes('medewerkerLabel')}
            aria-haspopup="true"
            color="secondary"
            onClick={() => dispatch(Selectie.SelectieActionCreator.FetchAllMedewerkers())}
            ><ReplayIcon /></IconButton>
        </MenuItem>
    }

    return <>
    <MenuItem onClick={handleProfileMenuOpen}>
        <Typography variant="body1" color="inherit">
            {text}
        </Typography>
        <IconButton
          aria-label={props.label || tDatatypes('medewerkerLabel')}
          aria-haspopup="true"
          color="inherit"
        ><SupervisedUserCircleIcon /></IconButton>
    </MenuItem>

    <Dialog
        open={Boolean(anchorEl)}
        onClose={() => setAnchorEl(null)}
        aria-labelledby={emptyLabel}
    >
        <DialogTitle id="alert-dialog-title">{tDatatypes('medewerkerPlaceholder')}</DialogTitle>
        <DialogContent className={classes.dialog}>
            <DialogActions>
                <MedewerkerSelect
                        syncWithRedux={props.syncWithRedux}
                        selectedMedewerker={selectedMedewerker}
                        label={props.label}
                        onChange={(medewerker?: Medewerker) => {
                            setAnchorEl(null);
                            setSelectedMedewerker(medewerker);
                            setText(medewerker ? medewerker.fullName : emptyLabel);
                            props.onChange && props.onChange(medewerker);
                        }}
                    />
            </DialogActions>
        </DialogContent>
    </Dialog>
    </>
}

// Legacy exports for backward compatibility - now deprecated
export interface IStateProps {
    medewerkers: any;
    selectedMedewerker: Medewerker;
}

export interface IStateDispatch {
    fetchMedewerkers: () => void;
    setSelectedMedewerker: (medewerker: Medewerker) => void;
}

export const mapAlleMedewerkersToProps = {
    state: (state: IApplicationState): IStateProps => ({
        medewerkers: state.selectie.allMedewerkers,
        selectedMedewerker: state.selectie.selectedMedewerker
    }),
    dispatch: (dispatch: any): IStateDispatch => ({
        fetchMedewerkers: () => dispatch(Selectie.SelectieActionCreator.FetchAllMedewerkers()),
        setSelectedMedewerker: (medewerker: Medewerker) => dispatch(Selectie.SelectieActionCreator.ActiveMedewerkerChanged(medewerker))
    })
}
